import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MessageSquare, CheckSquare, LayoutGrid, Plus, Trash2,
  Check, Clock, AlertTriangle, ChevronDown, ChevronUp, User,
  Calendar, PlayCircle, Tag, Target,
} from "lucide-react";
import { apiClient } from "../services/api";

const TABS = [
  { id: "dashboard", icon: LayoutGrid },
  { id: "meetings", icon: MessageSquare },
  { id: "actions", icon: CheckSquare },
];

const STATUS_STYLES = {
  scheduled: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const CATEGORY_STYLES = {
  update: "bg-blue-100 text-blue-700",
  blocker: "bg-red-100 text-red-700",
  feedback: "bg-yellow-100 text-yellow-700",
  recognition: "bg-green-100 text-green-700",
  growth: "bg-purple-100 text-purple-700",
  other: "bg-gray-100 text-gray-600",
};

const CATEGORY_KEYS = ["update", "blocker", "feedback", "recognition", "growth", "other"];
const RECURRENCE_KEYS = ["none", "weekly", "biweekly", "monthly"];
const statusLabel = (status, t) => t(`oneOnOneHub.status.${status}`, { defaultValue: status });
const categoryLabel = (category, t) => t(`oneOnOneHub.categories.${category}`, { defaultValue: category });
const recurrenceLabel = (recurrence, t) => t(`oneOnOneHub.recurrence.${recurrence}`, { defaultValue: recurrence });

export default function OneOnOneHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <MessageSquare className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("oneOnOneHub.title")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("oneOnOneHub.subtitle")}
            </p>
          </div>
          <span className="ml-auto bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">{t("oneOnOneHub.badgeNew")}</span>
        </div>

        <div className="flex gap-1 border-b">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(`oneOnOneHub.tabs.${tab.id}`)}
              </button>
            );
          })}
        </div>

        {activeTab === "dashboard" && <DashboardTab onNewMeeting={() => setTab("meetings")} />}
        {activeTab === "meetings" && <MeetingsTab />}
        {activeTab === "actions" && <ActionsTab />}
      </div>
    </div>
  );
}

/* ── Dashboard ──────────────────────────────────────────────────────────────── */
function DashboardTab({ onNewMeeting }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("v1/one-on-ones/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <LoadingState />;

  const kpis = [
    { label: t("oneOnOneHub.dashboard.kpiScheduled"), value: data.scheduled_meetings, icon: Clock, color: "text-blue-600" },
    { label: t("oneOnOneHub.dashboard.kpiCompleted"), value: data.completed_meetings, icon: Check, color: "text-green-600" },
    { label: t("oneOnOneHub.dashboard.kpiOpenActions"), value: data.open_action_items, icon: Target, color: "text-orange-500" },
    { label: t("oneOnOneHub.dashboard.kpiOverdueActions"), value: data.overdue_action_items, icon: AlertTriangle, color: "text-red-600" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-card border rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{k.label}</span>
                <Icon className={`h-5 w-5 ${k.color}`} />
              </div>
              <div className="text-2xl font-bold">{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Completion rate */}
      <div className="bg-card border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{t("oneOnOneHub.dashboard.completionRate")}</h3>
          <span className="text-lg font-bold">{data.completion_rate}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all"
            style={{ width: `${data.completion_rate}%` }}
          />
        </div>
      </div>

      {/* Upcoming */}
      <div className="bg-card border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{t("oneOnOneHub.dashboard.upcoming")}</h3>
          <button
            onClick={onNewMeeting}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <Plus className="h-3 w-3" /> {t("oneOnOneHub.dashboard.schedule")}
          </button>
        </div>
        {data.upcoming_meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("oneOnOneHub.dashboard.noUpcoming")}</p>
        ) : (
          <div className="space-y-2">
            {data.upcoming_meetings.map((m) => (
              <MeetingRow key={m.id} meeting={m} compact />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Meetings ───────────────────────────────────────────────────────────────── */
function MeetingsTab() {
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = () =>
    apiClient.get("v1/one-on-ones?limit=100").then((r) => setMeetings(r.data)).catch(() => {});

  useEffect(() => {
    load();
    apiClient.get("v1/employees/?limit=200").then((r) => {
      const emps = r.data?.employees || r.data?.results || r.data || [];
      setEmployees(Array.isArray(emps) ? emps : []);
    }).catch(() => {});
  }, []);

  const handleCreate = async (data) => {
    await apiClient.post("v1/one-on-ones", data);
    setShowModal(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("oneOnOneHub.meetings.confirmDelete"))) return;
    await apiClient.delete(`v1/one-on-ones/${id}`);
    load();
  };

  const handleComplete = async (id) => {
    await apiClient.post(`v1/one-on-ones/${id}/complete`, { meeting_notes: "" });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> {t("oneOnOneHub.meetings.scheduleMeeting")}
        </button>
      </div>

      {meetings.length === 0 ? (
        <EmptyState icon={MessageSquare} title={t("oneOnOneHub.meetings.empty")} description={t("oneOnOneHub.meetings.emptyHint")} />
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => (
            <MeetingCard
              key={m.id}
              meeting={m}
              onDelete={() => handleDelete(m.id)}
              onComplete={() => handleComplete(m.id)}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {showModal && (
        <CreateMeetingModal
          employees={employees}
          onSave={handleCreate}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function MeetingCard({ meeting, onDelete, onComplete, onRefresh }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [newTp, setNewTp] = useState("");
  const [newTpCat, setNewTpCat] = useState("update");
  const [newAction, setNewAction] = useState("");
  const [newActionDue, setNewActionDue] = useState("");
  const [loading, setLoading] = useState(false);

  const addTp = async () => {
    if (!newTp.trim()) return;
    setLoading(true);
    await apiClient.post(`v1/one-on-ones/${meeting.id}/talking-points`, { content: newTp, category: newTpCat });
    setNewTp("");
    setLoading(false);
    onRefresh();
  };

  const toggleTp = async (id) => {
    await apiClient.post(`v1/one-on-ones/talking-points/${id}/toggle`);
    onRefresh();
  };

  const deleteTp = async (id) => {
    await apiClient.delete(`v1/one-on-ones/talking-points/${id}`);
    onRefresh();
  };

  const addAction = async () => {
    if (!newAction.trim()) return;
    setLoading(true);
    await apiClient.post(`v1/one-on-ones/${meeting.id}/action-items`, {
      description: newAction,
      due_date: newActionDue || null,
    });
    setNewAction("");
    setNewActionDue("");
    setLoading(false);
    onRefresh();
  };

  const toggleAction = async (id) => {
    await apiClient.post(`v1/one-on-ones/action-items/${id}/toggle`);
    onRefresh();
  };

  const deleteAction = async (id) => {
    await apiClient.delete(`v1/one-on-ones/action-items/${id}`);
    onRefresh();
  };

  const tps = meeting.talking_points || [];
  const actions = meeting.action_items || [];
  const resolvedTps = tps.filter((t) => t.is_resolved).length;
  const completedActions = actions.filter((a) => a.is_completed).length;

  return (
    <div className="bg-card border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">
                {meeting.manager_name} <span className="text-muted-foreground font-normal">↔</span> {meeting.employee_name}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[meeting.status]}`}>
                {statusLabel(meeting.status, t)}
              </span>
              {meeting.recurrence !== "none" && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  {recurrenceLabel(meeting.recurrence, t)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {meeting.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString() : "—"}
              </span>
              <span>{t("oneOnOneHub.meetings.minutes", { count: meeting.duration_minutes })}</span>
              {tps.length > 0 && (
                <span>{t("oneOnOneHub.meetings.pointsDone", { done: resolvedTps, total: tps.length })}</span>
              )}
              {actions.length > 0 && (
                <span>{t("oneOnOneHub.meetings.actionsDone", { done: completedActions, total: actions.length })}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {meeting.status === "scheduled" && (
            <button
              onClick={onComplete}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
            >
              <PlayCircle className="h-3.5 w-3.5" /> {t("oneOnOneHub.meetings.complete")}
            </button>
          )}
          <button onClick={onDelete} className="p-1.5 hover:bg-accent rounded text-red-500">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-accent rounded">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-5">
          {/* Meeting notes */}
          {meeting.meeting_notes && (
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("oneOnOneHub.meetings.notes")}</p>
              <p className="text-sm whitespace-pre-wrap">{meeting.meeting_notes}</p>
            </div>
          )}

          {/* Talking points */}
          <div>
            <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-primary" /> {t("oneOnOneHub.meetings.agenda")}
            </p>
            <div className="space-y-1.5 mb-2">
              {tps.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("oneOnOneHub.meetings.noTalkingPoints")}</p>
              )}
              {tps.map((tp) => (
                <div key={tp.id} className="flex items-center gap-2">
                  <button onClick={() => toggleTp(tp.id)} className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${tp.is_resolved ? "bg-green-500 border-green-500" : "border-gray-300 hover:border-primary"}`}>
                    {tp.is_resolved && <Check className="h-2.5 w-2.5 text-white" />}
                  </button>
                  <span className={`flex-1 text-sm ${tp.is_resolved ? "line-through text-muted-foreground" : ""}`}>
                    {tp.content}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${CATEGORY_STYLES[tp.category]}`}>
                    {categoryLabel(tp.category, t)}
                  </span>
                  <button onClick={() => deleteTp(tp.id)} className="text-muted-foreground hover:text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <select
                value={newTpCat}
                onChange={(e) => setNewTpCat(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-background w-32 shrink-0"
              >
                {CATEGORY_KEYS.map((k) => (
                  <option key={k} value={k}>{categoryLabel(k, t)}</option>
                ))}
              </select>
              <input
                value={newTp}
                onChange={(e) => setNewTp(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTp()}
                placeholder={t("oneOnOneHub.meetings.addTalkingPoint")}
                className="flex-1 border rounded px-2 py-1 text-xs bg-background"
              />
              <button onClick={addTp} disabled={loading || !newTp.trim()} className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Action items */}
          <div>
            <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5 text-orange-500" /> {t("oneOnOneHub.meetings.followUpActions")}
            </p>
            <div className="space-y-1.5 mb-2">
              {actions.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("oneOnOneHub.meetings.noActions")}</p>
              )}
              {actions.map((a) => (
                <div key={a.id} className="flex items-center gap-2">
                  <button onClick={() => toggleAction(a.id)} className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${a.is_completed ? "bg-green-500 border-green-500" : "border-gray-300 hover:border-primary"}`}>
                    {a.is_completed && <Check className="h-2.5 w-2.5 text-white" />}
                  </button>
                  <span className={`flex-1 text-sm ${a.is_completed ? "line-through text-muted-foreground" : ""}`}>
                    {a.description}
                  </span>
                  {a.due_date && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${new Date(a.due_date) < new Date() && !a.is_completed ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                      {new Date(a.due_date).toLocaleDateString()}
                    </span>
                  )}
                  <button onClick={() => deleteAction(a.id)} className="text-muted-foreground hover:text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={newAction}
                onChange={(e) => setNewAction(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAction()}
                placeholder={t("oneOnOneHub.meetings.newAction")}
                className="flex-1 border rounded px-2 py-1 text-xs bg-background"
              />
              <input
                type="date"
                value={newActionDue}
                onChange={(e) => setNewActionDue(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-background w-28 shrink-0"
              />
              <button onClick={addAction} disabled={loading || !newAction.trim()} className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MeetingRow({ meeting, compact }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <User className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            {meeting.manager_name} ↔ {meeting.employee_name}
          </p>
          <p className="text-xs text-muted-foreground">
            {meeting.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString() : "—"}
            {" · "}{t("oneOnOneHub.meetings.minutes", { count: meeting.duration_minutes })}
          </p>
        </div>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_STYLES[meeting.status]}`}>
        {statusLabel(meeting.status, t)}
      </span>
    </div>
  );
}

/* ── Actions tracker ────────────────────────────────────────────────────────── */
function ActionsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  const load = () =>
    apiClient.get("v1/one-on-ones/action-items/open").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    await apiClient.post(`v1/one-on-ones/action-items/${id}/toggle`);
    load();
  };

  const isOverdue = (due) => due && new Date(due) < new Date();

  return (
    <div className="bg-card border rounded-xl overflow-hidden">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">{t("oneOnOneHub.actions.title")}</h3>
        <span className="text-sm text-muted-foreground">{t("oneOnOneHub.actions.count", { count: items.length })}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">
          <CheckSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>{t("oneOnOneHub.actions.empty")}</p>
        </div>
      ) : (
        <div className="divide-y">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30">
              <button
                onClick={() => toggle(item.id)}
                className="flex-shrink-0 w-5 h-5 rounded border-2 border-gray-300 hover:border-primary flex items-center justify-center"
              >
                <Check className="h-3 w-3 text-transparent hover:text-primary" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.description}</p>
                {item.assignee_name && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> {item.assignee_name}
                  </p>
                )}
              </div>
              {item.due_date && (
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1 ${isOverdue(item.due_date) ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                  {isOverdue(item.due_date) && <AlertTriangle className="h-3 w-3" />}
                  {new Date(item.due_date).toLocaleDateString()}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Create meeting modal ────────────────────────────────────────────────────── */
function CreateMeetingModal({ employees, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    manager_id: "",
    employee_id: "",
    scheduled_at: "",
    duration_minutes: 30,
    recurrence: "none",
  });

  const empOptions = employees.map((e) => ({
    id: e.id,
    label: `${e.first_name} ${e.last_name}${e.registration_number ? ` (${e.registration_number})` : ""}`,
  }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-xl w-full max-w-md">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold">{t("oneOnOneHub.modal.title")}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("oneOnOneHub.modal.manager")}</label>
            <select
              value={form.manager_id}
              onChange={(e) => setForm({ ...form, manager_id: parseInt(e.target.value) || "" })}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            >
              <option value="">{t("oneOnOneHub.modal.selectManager")}</option>
              {empOptions.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("oneOnOneHub.modal.employee")}</label>
            <select
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: parseInt(e.target.value) || "" })}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            >
              <option value="">{t("oneOnOneHub.modal.selectEmployee")}</option>
              {empOptions.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{t("oneOnOneHub.modal.dateTime")}</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("oneOnOneHub.modal.duration")}</label>
              <input
                type="number"
                min={15}
                max={120}
                step={15}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("oneOnOneHub.modal.recurrence")}</label>
            <select
              value={form.recurrence}
              onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
            >
              {RECURRENCE_KEYS.map((k) => (
                <option key={k} value={k}>{recurrenceLabel(k, t)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-accent">{t("oneOnOneHub.common.cancel")}</button>
          <button
            onClick={() => onSave({ ...form, scheduled_at: form.scheduled_at ? form.scheduled_at + ":00" : "" })}
            disabled={!form.manager_id || !form.employee_id || !form.scheduled_at}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {t("oneOnOneHub.modal.createCta")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function LoadingState() {
  const { t } = useTranslation();
  return <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">{t("oneOnOneHub.loading")}</div>;
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="text-center py-16 border rounded-xl bg-card">
      <Icon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
